<template>
    <div class="w-full bg-base-200 text-base-content/80">
        <footer class="container flex flex-row flex-wrap items-center justify-between gap-x-2 gap-y-6 p-4 py-12">
            <form class="flex w-full flex-wrap justify-center gap-4"
                  @focusin.once="preloadRecaptcha"
                  @submit.prevent="subscribe">
                <ClientOnly>
                    <Teleport to="body">
                        <TransitionSlide appear :offset="[0, -50]">
                            <div v-show="success"
                                 aria-live="polite"
                                 role="alert"
                                 tabindex="0"
                                 aria-label="Dismiss success message"
                                 class="alert alert-success fixed left-1/2 top-4 z-50 m-0 max-w-max -translate-x-1/2
                                        cursor-pointer bg-success/90 text-white"
                                 @click="success = false"
                                 @keydown.enter="success = false">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                     class="size-6 shrink-0 stroke-current"
                                     fill="none"
                                     viewBox="0 0 24 24">
                                    <path stroke-linecap="round"
                                          stroke-linejoin="round"
                                          stroke-width="2"
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                Thank you for subscribing!
                            </div>
                        </TransitionSlide>
                        <TransitionSlide appear :offset="[0, -50]">
                            <div v-show="errorMessage"
                                 aria-live="assertive"
                                 role="alert"
                                 tabindex="0"
                                 aria-label="Dismiss error message"
                                 class="alert alert-error fixed left-1/2 top-4 z-50 m-0 max-w-max -translate-x-1/2
                                        cursor-pointer bg-error/90 text-white"
                                 @click="errorMessage = ''"
                                 @keydown.enter="errorMessage = ''">
                                <svg xmlns="http://www.w3.org/2000/svg"
                                     class="size-6 shrink-0 stroke-current"
                                     fill="none"
                                     viewBox="0 0 24 24">
                                    <path stroke-linecap="round"
                                          stroke-linejoin="round"
                                          stroke-width="2"
                                          d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                {{ errorMessage }}
                            </div>
                        </TransitionSlide>
                    </Teleport>
                </ClientOnly>

                <div class="w-full grow text-center">
                    <h2 class="w-full grow text-base font-bold text-base-content/75">
                        Stay in the know
                    </h2>
                    <p>
                        Subscribe to our newsletter to get the latest updates, tips and tricks, and offers!
                    </p>
                </div>
                <input v-model="name"
                       autocomplete="name"
                       type="text"
                       class="input w-full sm:w-auto input-lg input-neutral"
                       name="name"
                       placeholder="Name"
                       required>
                <input v-model="email"
                       type="email"
                       name="email"
                       autocomplete="email"
                       class="input w-full sm:w-auto input-lg input-neutral"
                       placeholder="Email Address"
                       required>
                <button class="btn btn-primary 2xs:btn-wide ">
                    Subscribe
                </button>
                <span class="w-full text-center text-xs text-base-content/75">
                    This site is protected by reCAPTCHA and the Google
                    <a class="underline" href="https://policies.google.com/privacy">Privacy Policy</a> and
                    <a class="underline" href="https://policies.google.com/terms">Terms of Service</a> apply.
                </span>
            </form>

            <div class="mx-auto flex flex-col items-center gap-2 xs:mx-0">
                <Logo class="size-12 text-base-content/60 sm:size-20" />
                <p class="mb-2 text-xs text-base-content/75 xs:hidden">
                    2022 © by Cina Delicious Cakes
                </p>
            </div>

            <div class="flex items-center justify-between gap-x-12">
                <div>
                    <p class="mb-2 hidden text-xs text-base-content/75 xs:block">
                        2022 © by<br>Cina Delicious Cakes
                    </p>
                    <nav>
                        <p class="mb-2 select-none text-sm font-bold">
                            Socials:
                        </p>
                        <div class="flex items-center justify-evenly gap-2">
                            <NuxtLink external
                                      href="https://www.facebook.com/CinaCDC"
                                      target="_blank"
                                      aria-label="Facebook"
                                      class="inline-block"
                                      rel="noopener">
                                <Icon name="fa-brands:facebook" class="size-6" />
                            </NuxtLink>
                            <NuxtLink external
                                      href="https://www.instagram.com/cina_delicious_cakes/"
                                      target="_blank"
                                      aria-label="Instagram"
                                      class="inline-block"
                                      rel="noopener">
                                <Icon name="fa-brands:instagram" class="size-6" />
                            </NuxtLink>
                            <NuxtLink external
                                      href="https://www.tiktok.com/@cinasdeliciouscakes"
                                      target="_blank"
                                      aria-label="TikTok"
                                      class="inline-block"
                                      rel="noopener">
                                <Icon name="fa-brands:tiktok" class="size-6" />
                            </NuxtLink>
                        </div>
                    </nav>
                </div>

                <nav class="flex flex-col gap-y-2 text-center text-sm xs:text-left sm:text-base">
                    <NuxtLink :to="{ name: 'index' }">
                        Home Page
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'products' }">
                        Products
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'galleries' }">
                        Galleries
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'birthday-cakes' }">
                        Birthday Cakes
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'wedding-cakes' }">
                        Wedding Cakes
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'cupcakes' }">
                        Cupcakes
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'hungarian-cakes' }">
                        Hungarian Cakes
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'delivery' }">
                        Delivery &amp; Collection
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'about' }">
                        About
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'faqs' }">
                        Frequently Asked Questions
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'contact' }">
                        Contact
                    </NuxtLink>
                    <NuxtLink :to="{ name: 'cookie-policy' }">
                        Cookie Policy
                    </NuxtLink>
                </nav>
            </div>

            <p class="w-full select-none text-center text-xs text-base-content/75 md:text-left">
                Made by <NuxtLink external target="_blank" rel="noopener" href="https://nandor.kraszlan.dev">
                    Nandor Kraszlan
                </NuxtLink>
            </p>

            <div class="flex w-full flex-wrap items-center justify-center gap-2">
                <NuxtLink v-for="location in locations"
                          :key="location"
                          :to="{ name: 'slug-cakes', params: { slug: simpleSlug(location) } }"
                          class="text-xs text-base-content/75">
                    Cakes in {{ location }}
                </NuxtLink>
            </div>
        </footer>
    </div>
</template>

<script setup lang="ts">
import { locations } from '~/fixtures';
import { useRecaptchaV3 } from '~/composables/useRecaptchaV3';

const api = useApi();

defineOptions({
    name: 'FooterSection'
});

const name = ref('');
const email = ref('');
const success = ref<boolean>(false);
const errorMessage = ref<string>('');
const { execute, preload: preloadRecaptcha } = useRecaptchaV3('subscribe_form');

const subscribe = async () => {
    errorMessage.value = '';
    const resp = await execute();

    api('newsletter/subscribe', {
        method: 'POST',
        body: {
            name: name.value,
            email: email.value,
            challenge: resp
        }
    })
        .then(() => {
            useTrackEvent('newsletter-subscribe', {
                name: name.value,
                email: email.value
            });

            success.value = true;
            setTimeout(() => { success.value = false }, 4000);

            name.value = '';
            email.value = '';
        })
        .catch((err: any) => {
            errorMessage.value = err?.message ?? 'Something went wrong. Please try again.';
            setTimeout(() => { errorMessage.value = '' }, 5000);
        })
        .finally(() => {
            /* no-op */
        });
};
</script>
